import { Section, SectionParagraph, SectionTitle } from '@/components/shared/Content';
import JoinCTA from '@/components/shared/JoinCTA';
import filledCheck from '@/static/assets/pet/comparison/filled-check.svg';
import outlineCheck from '@/static/assets/pet/comparison/outline-check.svg';
import outlineUncheck from '@/static/assets/pet/comparison/outline-unchecked.svg';
import WhiteLemonadeLogo from '@/static/assets/pet/comparison/white-lemonade-logo.svg';
import arrowLeft from '@/static/pet/compare/arrow-left.svg';
import arrowRight from '@/static/pet/compare/arrow-right.svg';
import type { Product } from '@/utils/products';
import { Flex, font, preventOneWordInLastLine, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import Image from 'next/image';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { mergeRefs } from 'react-merge-refs';
import styled, { css } from 'styled-components';

const SMALL_SCREEN = '370px';

const Logo = styled(Flex)`
  ${font('main', { fontWeight: 'bold' })};
  height: 62px;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding-inline: 20px;
  ${MEDIA_QUERIES.desktop`
    min-width: 150px;
  `}
  @media (max-width: ${SMALL_SCREEN}) {
    padding-inline: 10px;
  }
`;

const LemonadeLogo = styled(Logo)`
  border-radius: 8px 8px 0 0;
  background-color: ${themedColor('primary')};
`;

const Row = styled(Flex)<{
  isEven: boolean;
  primaryColor?: string;
  secondaryColor?: string;
  isLast?: boolean;
}>`
  ${font('main', { fontWeight: 'bold' })};
  align-items: center;
  height: 81px;
  align-items: center;
  padding-inline: 20px;
  background-color: ${props =>
    props.isEven
      ? props.secondaryColor
        ? props.secondaryColor
        : themedColor('white')
      : props.primaryColor
        ? props.primaryColor
        : themedColor('cloud')};

  ${({ isLast }) =>
    isLast &&
    css`
      border-radius: 0 0 8px 8px;
      margin-bottom: 16px;
    `}

  @media (max-width: ${SMALL_SCREEN}) {
    padding-inline: 10px;
  }
`;

const FullReviewLink = styled.a`
  ${font('main')};
  color: ${themedColor('textPrimary')};
  text-decoration: underline;
  margin-top: 16px;
  text-align: center;
`;

const ScrollableSectionWrapper = styled(Flex)`
  overflow-x: auto;
  width: auto;
  max-width: 610px;
  width: 100%;
  -ms-overflow-style: none;
  scrollbar-width: none;
  scroll-behavior: smooth;
  &::-webkit-scrollbar {
    display: none;
  }
  scroll-snap-type: x mandatory;
  & > * {
    scroll-snap-align: start;
  }
`;

const ScrollHandler = styled(Image)<{ disabled: boolean }>`
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.4 : 1)};
  filter: ${props => (props.disabled ? 'grayscale(1)' : 'none')};
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
  transition: all ease 0.3s;
  &:hover {
    transform: ${props => (props.disabled ? 'scale(1.13)' : 'scale(1)')};
  }
`;

const ScoreValue = styled.span`
  font-weight: normal;
`;

const StyledCriteria = styled.span`
  font-size: 14px;
  @media (max-width: ${SMALL_SCREEN}) {
    font-size: 12px;
  }
  ${MEDIA_QUERIES.tabletAndDesktop`
    font-size: 16px;
  `}
`;

const StyledCta = styled(JoinCTA)`
  @media (max-width: ${SMALL_SCREEN}) {
    font-size: 12px;
  }
`;

type Value = string | boolean;

interface Company {
  companyName: string;
  companyLogo?: StaticImageData | string;
  values: Value[];
  reviewLink: string;
}

export interface CompareTableProps {
  title: string;
  subtitle: string;
  comparisonCriteria: string[];
  lemonade: {
    values: Value[];
    product: Product;
    ctaLabel: string;
  };
  companies: Company[];
  darkBackground?: boolean;
}

interface RenderValueProps {
  value: Value;
  checkImage?: StaticImageData;
  unCheckImage?: StaticImageData;
}

const RenderValue: React.FC<RenderValueProps> = ({ value, checkImage, unCheckImage }) => {
  if (typeof value === 'string') return <ScoreValue>{value}</ScoreValue>;
  return <Image src={value ? (checkImage ?? outlineCheck) : (unCheckImage ?? outlineUncheck)} alt="" />;
};

const RenderCompanyLogo: React.FC<{ company: Company }> = ({ company }) => {
  return (
    <Logo>
      {company?.companyLogo != null ? <Image src={company.companyLogo} alt="" width={90} /> : company.companyName}
    </Logo>
  );
};

export const CompareTable: React.FC<CompareTableProps> = ({
  title,
  subtitle,
  comparisonCriteria,
  lemonade,
  companies,
  darkBackground,
}) => {
  const [scrollPosition, setScrollPosition] = useState({
    start: true,
    end: false,
  });
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });
  const scrollableSectionRef = useRef<HTMLDivElement>(null);

  const onScroll = useCallback((direction: 'left' | 'right') => {
    if (scrollableSectionRef.current) {
      const scrollableSection = scrollableSectionRef.current;
      const scrollAmount = 100; // lowest possible value, snap will take effect either way
      if (direction === 'left') {
        scrollableSection.scrollLeft -= scrollAmount;
      } else {
        scrollableSection.scrollLeft += scrollAmount;
      }
    }
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (inView) {
      timer = setTimeout(() => {
        onScroll('left');
      }, 500);
      onScroll('right');
    }

    return () => clearTimeout(timer);
  }, [inView, onScroll]);

  const handleScroll = useCallback((event: Event) => {
    const target = event.target as HTMLElement;
    const scrollLeft = target.scrollLeft;
    const scrollWidth = target.scrollWidth - target.clientWidth;
    setScrollPosition({
      start: scrollLeft === 0,
      end: scrollLeft === scrollWidth,
    });
  }, []);

  useEffect(() => {
    if (scrollableSectionRef.current) {
      const scrollableSection = scrollableSectionRef.current;
      scrollableSection.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollableSectionRef.current) {
        const scrollableSection = scrollableSectionRef.current;
        scrollableSection.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  return (
    <Section py={[40, 70]} mx="auto" darkBackground={darkBackground}>
      <Flex flexDirection="column" gap="20px" mb="30px" px="20px">
        <SectionTitle>{title}</SectionTitle>
        <SectionParagraph>{subtitle}</SectionParagraph>
      </Flex>

      <Flex alignItems="center" justifyContent="center" gap="16px" mb="60px">
        <ScrollHandler src={arrowLeft} alt="" onClick={() => onScroll('left')} disabled={scrollPosition.start} />
        <SectionParagraph>Scroll to compare</SectionParagraph>
        <ScrollHandler src={arrowRight} alt="" onClick={() => onScroll('right')} disabled={scrollPosition.end} />
      </Flex>

      <Flex width="100%" justifyContent="center">
        <Flex flexDirection="column" flex="1 1 0%" maxWidth="350px">
          <Logo />
          {comparisonCriteria.map((criteria, index) => (
            <Row isEven={index % 2} key={criteria}>
              <StyledCriteria>{preventOneWordInLastLine(criteria)}</StyledCriteria>
            </Row>
          ))}
        </Flex>

        <Flex flexDirection="column" flex="1 1 0%" maxWidth="150px">
          <LemonadeLogo>
            <Image src={WhiteLemonadeLogo} alt="" />
          </LemonadeLogo>
          {lemonade.values.map((value, index) => (
            <Row
              secondaryColor="#FFF7FB"
              primaryColor={themedColor('lightPink')}
              isEven={index % 2}
              key={`${value.toString()}-lemonade`}
              justifyContent="center"
              isLast={lemonade.values.length - 1 === index}
            >
              <RenderValue value={value} checkImage={filledCheck} />
            </Row>
          ))}
          <StyledCta
            product={lemonade.product}
            flavor="primary"
            glowing
            data-event="pet_compare_landing_page.get_quote.clicked"
            data-event-Param-Section="comparison table"
          >
            {lemonade.ctaLabel}
          </StyledCta>
        </Flex>

        <ScrollableSectionWrapper ref={mergeRefs([scrollableSectionRef, ref])} flex="1 1 0%">
          {companies.map(company => (
            <Flex flexDirection="column" key={company.companyName} width="100%">
              <RenderCompanyLogo company={company} />
              {company.values.map((value, index) => (
                <Row
                  isEven={index % 2}
                  key={`${value.toString()}-lemonade-${company.companyName}`}
                  justifyContent="center"
                  alignItems="center"
                  isLast={company.values.length - 1 === index}
                >
                  <RenderValue value={value} />
                </Row>
              ))}
              <FullReviewLink
                href={company.reviewLink}
                data-event="pet_compare_landing_page.full_review.clicked"
                data-event-Param-Competitor={company.companyName}
              >
                Full review
              </FullReviewLink>
            </Flex>
          ))}
        </ScrollableSectionWrapper>
      </Flex>
    </Section>
  );
};
